import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Box, Button, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import psTheme, { backdrop, surfaces } from '../ps/psTheme';
import '../ps/ps-tokens.css';
import '../ps/learn.css';
import loader from './loader.png';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './customVideoStyles.css';
import Hls from 'hls.js';
import throttle from 'lodash.throttle';
import pslogo from './pslogo.png';
import { PlayerBar, TitleBlock } from './PlayerChrome';
import useSubtitles from './useSubtitles';
import TheCB from './TheCB';
import { hasVideoChat } from './videoChat';

const VPlayer = () => {
  const { videoName } = useParams();
  const location = useLocation();
  const [isPromo, setIsPromo] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null); // Add ref for HLS instance to prevent memory leak
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const DataReady = useRef(null);
  const [showHider, setshowHider] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [allowChatbot, setallowChatbot] = useState(false);

  // English by default, and whatever you pick from then on.
  const { selected: selectedSubtitle, select: handleSubtitleSelect } = useSubtitles({
    playerRef,
    subtitles: videoData?.subtitles,
    isPlayerReady,
  });

  useEffect(() => {
    // Parse URL parameters and set isPromo state
    const searchParams = new URLSearchParams(location.search);
    setIsPromo(searchParams.get('promo') === 'true');
    setIsUnlocked(searchParams.get('unlckd') === 'true');
    setallowChatbot(searchParams.get('chatbot') === 'true');
  }, [location]);

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setLoading(false);
    }, 900);

    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    if (playerRef.current && isPlayerReady) {
      const player = playerRef.current;

      // One state update a second: enough for the chapter strip to track the
      // playhead smoothly, and still 1/4 of what `timeupdate` fires. At the
      // old 10s it lurched a tenth of a chapter at a time.
      const updateTime = throttle(() => {
        const newTime = Math.floor(player.currentTime());
        setCurrentTime(newTime);
      }, 1000);

      player.on('timeupdate', updateTime);
      player.on('play', updateTime);

      return () => {
        player.off('timeupdate', updateTime);
        player.off('play', updateTime);
        updateTime.cancel(); // Cancel any pending throttled calls
      };
    }
  }, [playerRef, isPlayerReady]);

  const orderSubtitles = (videoData) => {
    if (!videoData || !videoData.subtitles || !Array.isArray(videoData.subtitles)) {
      return [];
    }

    // Define the preferred order of languages
    const languageOrder = ['en', 'de', 'fr', 'it', 'es'];

    // Create a map for O(1) lookup of language positions
    const orderMap = Object.fromEntries(
      languageOrder.map((lang, index) => [lang, index])
    );

    // Sort the subtitles array
    const orderedSubtitles = [...videoData.subtitles].sort((a, b) => {
      const indexA = orderMap[a.srclang] ?? Number.MAX_SAFE_INTEGER;
      const indexB = orderMap[b.srclang] ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });

    return orderedSubtitles;
  };




  const fetchUserOwned = async () => {
    if (isUnlocked) {
      setshowHider(false);
      return;
    }


    const storedUserData = JSON.parse(localStorage.getItem('userData'));

    if (!storedUserData) {
      setshowHider(true);
      return;
    }

    if (storedUserData.token && !isUnlocked) {
    try {
      const response = await axios.post('https://psproducts.vercel.app/api/ownedfortoken', {
        token: storedUserData.token,
      });

      if (videoData.products) {
        videoData.products = videoData.products.split(/\s*,\s*/).map((product) => product.trim());
      } else {
        videoData.products = [];
      }

      let owned = false;
      if (response.data.subscription === 'active') {
        owned = true;
      } else {
        owned = videoData.products.some((product) => response.data.products.includes(product));
      }

      if (owned === false) {
        setshowHider(true);
      }
      else {
        setshowHider(false);
      }





    } catch (err) {
      // Error fetching user data
    }
  }
  };

  // Modified useEffect with ordered subtitles
  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        const response = await axios.get(
          `https://learn.philspeiser.com/api/getvideo?identifier=${videoName}`
        );
        const videoInfo = response.data;

        if (videoInfo) {

          // 1. Check for the HLS and MP4 URLs and replace if they start with "https://vod.api.video/vod/"
          if (videoInfo.assets) {
            if (videoInfo.assets.hls && videoInfo.assets.hls.startsWith('https://vod.api.video/vod/')) {
              videoInfo.assets.hls = videoInfo.assets.hls.replace(
                'https://vod.api.video/vod/',
                'https://psvids.b-cdn.net/'
              );
            }
            if (videoInfo.assets.mp4 && videoInfo.assets.mp4.startsWith('https://vod.api.video/vod/')) {
              videoInfo.assets.mp4 = videoInfo.assets.mp4.replace(
                'https://vod.api.video/vod/',
                'https://psvids.b-cdn.net/'
              );
            }
          }

                    // 2. Check for the thumbnail URL and replace if needed
          if (
            videoInfo.thumbnailUrl &&
            videoInfo.thumbnailUrl.startsWith('https://vod.api.video/vod/')
          ) {
            videoInfo.thumbnailUrl = videoInfo.thumbnailUrl.replace(
              'https://vod.api.video/vod/',
              'https://psvids.b-cdn.net/'
            );
          }

          // Replace Vercel Blob Storage with BunnyCDN
          if (
            videoInfo.thumbnailUrl &&
            videoInfo.thumbnailUrl.includes('i6bcizwg1mpghn9b.public.blob.vercel-storage.com')
          ) {
            videoInfo.thumbnailUrl = videoInfo.thumbnailUrl.replace(
              'i6bcizwg1mpghn9b.public.blob.vercel-storage.com',
              'psblob.b-cdn.net'
            );
          }



          // 3. Now set the final thumbnail (fallback if none)
          videoInfo.thumbnail =
            videoInfo.thumbnailUrl ??
            'https://i6bcizwg1mpghn9b.public.blob.vercel-storage.com/videothumbnails/fallback-bDkvltu910WiV6ZVUREx2y3iICi8PQ.jpg';

          // 4. Order subtitles (assuming you have an orderSubtitles helper)
          videoInfo.subtitles = orderSubtitles(videoInfo);

          setVideoData(videoInfo);
        }
      } catch (error) {
        // Error fetching video data
      }
    };

    fetchVideoData();
  }, [videoName]);


  useEffect(() => {
    return () => {
      // Clean up HLS instance to prevent memory leak
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // Clean up Video.js player
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);


  useEffect(() => {

    if (videoData && DataReady.current !== 1 && videoData.public !== "true" && isUnlocked === false) {
      fetchUserOwned();
      DataReady.current = 1;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoData]);

  const handleChapterClick = useCallback((time) => {
    if (playerRef.current) {
      // Ensure the time is within video duration
      const safeTime = time < videoDuration ? time : videoDuration - 1;

      playerRef.current.currentTime(Math.round(safeTime));
      playerRef.current.play();
    }
  }, [videoDuration]);

  return (
    <ThemeProvider theme={psTheme}>
      <CssBaseline />
      <Box className="learn-root" sx={backdrop}>
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'var(--ps-bg-deep)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <Box
              sx={{
                width: '45px',
                height: '45px',
                animation: 'loader-animation 0.8s steps(19) infinite',
                backgroundImage: `url(${loader})`,
                backgroundSize: '45px 885px',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <style>
              {`
                @keyframes loader-animation {
                  0% { background-position: 0 0; }
                  100% { background-position: 0 -885px; }
                }
              `}
            </style>
          </Box>
        )}

        {/* The gate. It sits on the backdrop rather than a black sheet, and
            what it asks for is a chip, not a 50px-wide button. */}
        {showHider && (
          <Box
            sx={{
              ...backdrop,
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1999,
              p: '10px',
            }}
          >
            <Box
              sx={{
                ...surfaces.panel,
                p: '22px',
                maxWidth: 420,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'center',
              }}
            >
              <img src={pslogo} alt="Phil Speiser" style={{ height: 18, marginBottom: 12 }} />
              <span className="ps-label">Do you own the plugin this video is about?</span>
              <Box sx={{ display: 'flex', gap: '4px', mt: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => { window.location.href = 'https://learn.philspeiser.com/'; }}
                >
                  Log in
                </Button>
                {videoData?.link && videoData?.linktext && (
                  <Button component="a" href={videoData.link} target="_blank" rel="noopener noreferrer">
                    {videoData.linktext}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}

        <header className="learn-header">
          <img className="learn-header__logo" src={pslogo} alt="Phil Speiser" />
        </header>

        {videoData && (
          <Box
            sx={{
              width: { xs: '100%', sm: '72%' },
              margin: 'auto',
              px: { xs: '10px', sm: 0 },
              pt: '64px',
              pb: '96px',
              maxWidth: '1400px',
            }}
          >
            <div className="learn-stage">
              <div data-vjs-player>
                <video
                  ref={(el) => {
                    videoRef.current = el;

                    if (
                      el &&
                      videoData &&
                      (videoData.assets && (videoData.assets.hls || videoData.assets.mp4)) &&
                      !playerRef.current
                    ) {
                      const videoElement = el;

                      playerRef.current = videojs(videoElement, {
                        controls: true,
                        autoplay: false,
                        preload: 'auto',
                        poster: videoData.thumbnail, // Always use widescreen thumbnail
                        responsive: true,
                        crossOrigin: 'anonymous',
                        fluid: true,
                        // Without this, iPhone Safari hijacks play into its
                        // native fullscreen player, where the custom skin and
                        // the overlay-rendered subtitles do not exist.
                        playsinline: true,
                        html5: {
                          nativeTextTracks: false,
                        },
                        controlBar: {
                          pictureInPictureToggle: false,
                          captionsButton: false,
                          subtitlesButton: false,
                          subsCapsButton: false,
                        },
                      });

                      // Add subtitles
                      if (videoData.subtitles && videoData.subtitles.length > 0) {
                        videoData.subtitles.forEach((subtitle) => {
                          playerRef.current.addRemoteTextTrack(
                            {
                              kind: 'subtitles',
                              label: subtitle.label,
                              srclang: subtitle.srclang,
                              src: subtitle.src,
                              default: subtitle.default,
                              crossOrigin: 'anonymous',
                            },
                            false
                          );
                        });
                      }

                      // Initialize with horizontal assets first (for proper aspect ratio)
                      const initialAssets = videoData.assets;

                      if (Hls.isSupported() && initialAssets.hls) {
                        hlsRef.current = new Hls({
                          startLevel: 3,
                          capLevelToPlayerSize: false,
                          // The manifest carries the same captions the remote
                          // VTT tracks above already render (they exist for
                          // iPhone's native fullscreen player); parsing the
                          // manifest's copy too would show every cue twice.
                          enableWebVTT: false,
                          enableIMSC1: false,
                        });
                        hlsRef.current.loadSource(initialAssets.hls);
                        hlsRef.current.attachMedia(videoElement);
                      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl') && initialAssets.hls) {
                        videoElement.src = initialAssets.hls;
                      } else if (initialAssets.mp4) {
                        videoElement.src = initialAssets.mp4;
                      } else {
                        // No compatible video format found
                      }

                      // Listen for metadata to get video duration
                      playerRef.current.on('loadedmetadata', () => {
                        const duration = playerRef.current.duration();
                        setVideoDuration(duration);


                        // Adjust chapters if necessary
                        if (videoData.chapters && videoData.chapters.length > 0) {
                          const adjustedChapters = videoData.chapters.map((chapter, index) => {
                            if (index === videoData.chapters.length - 1 && chapter.time >= duration) {
                              return { ...chapter, time: duration - 1 };
                            }
                            return chapter;
                          });
                          setVideoData((prevData) => ({
                            ...prevData,
                            chapters: adjustedChapters,
                          }));
                        }
                      });

                      // Error handling
                      playerRef.current.on('error', () => {
                        // Optionally, display a user-friendly message or attempt to recover
                      });

                      setIsPlayerReady(true);
                    }
                  }}
                  className="video-js vjs-big-play-centered custom-player"
                  crossOrigin="anonymous"
                  playsInline
                />
              </div>
            </div>

            <PlayerBar
              subtitles={videoData.subtitles}
              selectedSubtitle={selectedSubtitle}
              onSubtitleSelect={handleSubtitleSelect}
              chapters={videoData.chapters}
              currentTime={currentTime}
              duration={videoDuration}
              onChapterClick={handleChapterClick}
            />

            <TitleBlock video={videoData} />

            {isPromo && videoData.link && videoData.linktext && (
              <Box sx={{ mt: '22px' }}>
                <Button
                  variant="contained"
                  component="a"
                  href={videoData.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {videoData.linktext}
                </Button>
              </Box>
            )}

            {hasVideoChat(videoData) && allowChatbot && (
              <TheCB identifier={videoName} timestamp={currentTime} />
            )}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default VPlayer;
